import { collection, doc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { resolveFleetExpenseCategoriesCollectionPath } from '../../../core/company/companyPaths';

export const DEFAULT_FLEET_EXPENSE_CATEGORIES = Object.freeze([
  {
    id: 'combustible',
    name: 'Combustible',
    color: '#0ea5e9',
    order: 10,
    isFixed: true,
    isSystem: true,
    impactTransport: true,
    impactDriver: false,
    requiresDriverSelection: false,
    scopeLabel: 'Transporte',
    description: 'Consumo operativo de combustible de la unidad.',
  },
  {
    id: 'mantenimiento',
    name: 'Mantenimiento',
    color: '#6366f1',
    order: 20,
    isFixed: true,
    isSystem: true,
    impactTransport: true,
    impactDriver: false,
    requiresDriverSelection: false,
    scopeLabel: 'Transporte',
    description: 'Controles y tareas preventivas de la unidad.',
  },
  {
    id: 'reparacion',
    name: 'Reparación',
    color: '#f97316',
    order: 30,
    isFixed: true,
    isSystem: true,
    impactTransport: true,
    impactDriver: false,
    requiresDriverSelection: false,
    scopeLabel: 'Transporte',
    description: 'Reparaciones correctivas o extraordinarias de la unidad.',
  },
  {
    id: 'service',
    name: 'Service',
    color: '#14b8a6',
    order: 40,
    isFixed: true,
    isSystem: true,
    impactTransport: true,
    impactDriver: false,
    requiresDriverSelection: false,
    scopeLabel: 'Transporte',
    description: 'Servicios programados y control periódico.',
  },
  {
    id: 'cubiertas',
    name: 'Cubiertas',
    color: '#8b5cf6',
    order: 50,
    isFixed: true,
    isSystem: true,
    impactTransport: true,
    impactDriver: false,
    requiresDriverSelection: false,
    scopeLabel: 'Transporte',
    description: 'Neumáticos y trabajos vinculados al rodado.',
  },
  {
    id: 'peajes',
    name: 'Peajes',
    color: '#10b981',
    order: 60,
    isFixed: true,
    isSystem: true,
    impactTransport: true,
    impactDriver: false,
    requiresDriverSelection: false,
    scopeLabel: 'Transporte',
    description: 'Costos de circulación asociados a la unidad.',
  },
  {
    id: 'repuestos',
    name: 'Repuestos',
    color: '#f59e0b',
    order: 70,
    isFixed: true,
    isSystem: true,
    impactTransport: true,
    impactDriver: false,
    requiresDriverSelection: false,
    scopeLabel: 'Transporte',
    description: 'Componentes o piezas reemplazadas de la unidad.',
  },
  {
    id: 'seguro',
    name: 'Seguro',
    color: '#64748b',
    order: 80,
    isFixed: true,
    isSystem: true,
    impactTransport: true,
    impactDriver: false,
    requiresDriverSelection: false,
    scopeLabel: 'Transporte',
    description: 'Coberturas, pólizas o franquicias vinculadas a la unidad.',
  },
  {
    id: 'habilitacion',
    name: 'Habilitación',
    color: '#06b6d4',
    order: 90,
    isFixed: true,
    isSystem: true,
    impactTransport: true,
    impactDriver: false,
    requiresDriverSelection: false,
    scopeLabel: 'Transporte',
    description: 'Documentación, VTV o habilitaciones de circulación.',
  },
  {
    id: 'multas',
    name: 'Multas',
    color: '#ef4444',
    order: 100,
    isFixed: true,
    isSystem: true,
    impactTransport: true,
    impactDriver: true,
    requiresDriverSelection: true,
    scopeLabel: 'Chofer + transporte',
    description: 'Sanciones que impactan en la unidad y en el chofer involucrado.',
  },
  {
    id: 'incidentes',
    name: 'Incidentes',
    color: '#ec4899',
    order: 110,
    isFixed: true,
    isSystem: true,
    impactTransport: true,
    impactDriver: true,
    requiresDriverSelection: true,
    scopeLabel: 'Chofer + transporte',
    description: 'Eventos operativos que dejan registro cruzado entre unidad y chofer.',
  },
]);

export const sortFleetCategories = (categories = []) => {
  return [...categories].sort((a, b) => {
    const orderA = Number.isFinite(Number(a?.order)) ? Number(a.order) : 9999;
    const orderB = Number.isFinite(Number(b?.order)) ? Number(b.order) : 9999;
    if (orderA !== orderB) return orderA - orderB;
    return String(a?.name || '').localeCompare(String(b?.name || ''), 'es', { sensitivity: 'base' });
  });
};

export const ensureDefaultFleetExpenseCategories = async ({ db, companyId, companyName = '' }) => {
  if (!db || !companyId) return { ensured: [] };

  const categoriesPath = resolveFleetExpenseCategoriesCollectionPath(companyId);
  const categoriesRef = collection(db, categoriesPath);
  const snapshot = await getDocs(categoriesRef);
  const existingDocs = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
  const normalizeName = (value = '') => String(value || '').trim().toLowerCase();
  const existingById = new Map(existingDocs.map((item) => [item.id, item]));
  const existingByName = new Map(existingDocs.map((item) => [normalizeName(item.name), item]));
  const ensured = [];

  await Promise.all(
    DEFAULT_FLEET_EXPENSE_CATEGORIES.map(async (category) => {
      const existingBySameId = existingById.get(category.id);
      const existingBySameName = existingByName.get(normalizeName(category.name));
      const targetId = existingBySameId?.id || existingBySameName?.id || category.id;
      const categoryRef = doc(db, categoriesPath, targetId);

      if (existingBySameId?.isSystem && existingBySameId?.isFixed) return;

      await setDoc(categoryRef, {
        ...category,
        companyId,
        companyName,
        updatedAt: serverTimestamp(),
        ...(existingBySameId || existingBySameName ? {} : { createdAt: serverTimestamp() }),
      }, { merge: true });
      ensured.push(targetId);
    })
  );

  return { ensured };
};
